/**
 * @fileoverview Hook for managing audio streaming functionality, including playback and recording
 *
 * @example
 * // Usage:
 * import { useAudioStreamer } from './useAudioStreamer'
 *
 * function Component() {
 *   const [player, recorder, isConnected, error] = useAudioStreamer(1)
 *
 *   // Play audio
 *   await player.play('audio.mp3')
 *
 *   // Record audio
 *   await recorder.start()
 *   const audioData = await recorder.stop()
 *
 *   // Check connection status
 *   if (!isConnected) {
 *     return <div>Connecting...</div>
 *   }
 *
 *   // Handle errors
 *   if (error) {
 *     return <div>Error: {error.message}</div>
 *   }
 * }
 */


import i18n from 'i18next'
import { useContext, useEffect, useMemo, useState } from 'react'
import { AudioStreamer } from '../AudioStreamer'
import { RTCContext } from '../RTCContext'
import { useTimer } from './useTimer'



/**
 * @interface Player
 * @description Interface for audio playback functionality
 * @property {Function} play - Function to play audio from a URL
 * @property {Function} skip - Function to skip current audio playback
 * @property {Object} track - Object containing playback state
 * @property {number} track.currentTime - Current playback time in seconds
 * @property {boolean} track.isEnded - Whether playback has ended
 */
export interface Player {
    play: (audio: string) => Promise<MediaStream>
    skip: () => Promise<void>
    track: {
        currentTime: number
        isEnded: boolean
    }
}

/**
 * @interface Recorder
 * @description Interface for audio recording functionality
 * @property {Function} start - Function to start recording audio
 * @property {Function} stop - Function to stop recording and get audio data
 * @property {AnalyserNode} analyser - Web Audio API analyser node for visualizations
 * @property {number} currentTime - Current recording time in seconds
 */
export interface Recorder {
    start: () => Promise<string>
    stop: () => Promise<void>
    analyser: AnalyserNode
    currentTime: number
}


/**
 * @function useAudioStreamer
 * @description Hook for managing audio streaming functionality, including playback and recording
 * @param {number} attempt - Retry attempt number for connection
 * @returns {[Player, Recorder, boolean, unknown]} Tuple containing:
 *   - Player object for audio playback
 *   - Recorder object for audio recording
 *   - Boolean indicating connection status
 *   - Any error that occurred
 */
export function useAudioStreamer(attempt: number): [Player, Recorder, boolean, unknown] {
    const [startTimer, stopTimer, timer] = useTimer()
    const [streamAnalyser, setStreamAnalyser] = useState<AnalyserNode>()
    const [isConnected, setIsConnected] = useState<boolean>(false)
    const [isEnded, setIsEnded] = useState<boolean>(false)
    const [error, setError] = useState<unknown>()
    const config = useContext(RTCContext)
    const streamer = useMemo<AudioStreamer>(
        () => new AudioStreamer(config),
        [config.iceServers?.length, attempt, i18n.language]
    )

    /**
     * @function onTrackStart
     * @description Function to start the timer when a track starts
     */
    function onTrackStart() {
        startTimer()
    }

    /**
     * @function onTrackEnd
     * @description Function to stop the timer when a track ends
     */
    function onTrackEnd() {
        setIsEnded(true)
        stopTimer()
    }

    /**
     * @function onConnected
     * @description Function to set connection status to true when connected
     */
    function onConnected() {
        setIsConnected(true)
    }

    /**
     * @function onDisconnected
     * @description Function to set connection status to false when disconnected
     */
    function onDisconnected() {
        setIsConnected(false)
    }

    /**
     * @function onError
     * @description Function to set error state when an error occurs
     * @param {unknown} e - The error that occurred
     */
    function onError(e: unknown) {
        setError(e)
    }

    useEffect(() => {
        streamer.ee.addListener('error', onError)
        streamer.ee.addListener('track.start', onTrackStart)
        streamer.ee.addListener('track.end', onTrackEnd)
        streamer.ee.addListener('connected', onConnected)
        streamer.ee.addListener('disconnected', onDisconnected)

        return () => {
            streamer.ee.removeListener('error', onError)
            streamer.ee.removeListener('track.start', onTrackStart)
            streamer.ee.removeListener('track.end', onTrackEnd)
            streamer.ee.removeListener('connected', onConnected)
            streamer.ee.removeListener('disconnected', onDisconnected)
        }
    }, [streamer])

    useEffect(() => {
        if (config) {
            streamer.connect()
        }
    }, [config.iceServers?.length, attempt])

    /**
     * @function start
     * @description Function to start recording audio and get the recording UUID
     * @returns {Promise<string>} The UUID of the recording
     */
    async function start(): Promise<string> {
        const recordingUUID = await streamer.start()
        const analyser = streamer.getStreamAnalyser()

        setStreamAnalyser(analyser)

        startTimer()
        return recordingUUID
    }

    /**
     * @function stop
     * @description Function to stop recording audio and stop the timer
     * @returns {Promise<void>}
     */
    async function stop(): Promise<void> {
        await streamer.stop()
        stopTimer()
    }

    /**
     * @function play
     * @description Function to play audio from a URL
     * @param {string} audio - The URL of the audio to play
     * @returns {Promise<MediaStream>} The media stream for the audio playback
     */
    async function play(audio: string): Promise<MediaStream> {
        setIsEnded(false)
        const stream = await streamer.play(audio)

        return stream
    }

    /**
     * @function skip
     * @description Function to skip current audio playback
     * @returns {Promise<void>}
     */
    async function skip(): Promise<void> {
        await streamer.skip()
    }

    const player: Player = {
        play,
        skip,
        track: {
            currentTime: timer,
            isEnded,
        },
    }

    const recorder: Recorder = {
        start,
        stop,
        analyser: streamAnalyser,
        currentTime: timer,
    }

    return [player, recorder, isConnected, error]
}
